import { useEffect, useMemo, useRef, useState } from "react";
import { useIsFetching, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import {
  useActOnPriceResearch, getListPriceResearchQueryKey, type PriceResearchChange,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  AlertTriangle, ArrowLeft, Hammer, Download, FileUp, History, Loader2, RefreshCw, Scale, Search, ShieldAlert, ShieldCheck, Wrench,
} from "lucide-react";
import AdminRecipeDraftsPage from "@/pages/admin-recipe-drafts";
import { errorMessage } from "@/components/cost-recipe-panel";
import { usePriceResearchOverview } from "@/hooks/use-price-research-overview";
import { PriceReviewImportDialog, downloadPriceTemplate } from "@/components/price-review-import-dialog";
import { PriceReviewProposalDialog } from "@/components/price-review-proposal-dialog";
import { PriceReviewHistory } from "@/components/price-review-history";
import { PriceReviewUndo } from "@/components/price-review-undo";
import { PriceReviewPagination } from "@/components/price-review-pagination";
import {
  DECISIONS, DECISION_LABELS, VERDICTS, approvalBlocker, asDoc, batchType, isLabourEntry, libraryPrice, missingPairIds, safeFileUrl, gbp, priceDiff, signedGbp, signedPct, tabFromParam, proposalUpdateLabel, priceRecommendation, retainsExistingPrice,
  type ApplyPreview, type BatchDetail, type CatalogueEntry, type Decision, type LabourEntry, type Proposal, type ResearchType, type ReviewOverview, type Verdict,
} from "@/lib/price-review";

const VERDICT_TONE: Record<Verdict, string> = {
  Accurate: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/50 dark:text-emerald-100",
  "May need updating": "bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100",
  "Must be updated": "bg-rose-100 text-rose-900 dark:bg-rose-900/50 dark:text-rose-100",
};
const DECISION_TONE: Record<Decision, string> = {
  pending: "bg-muted text-muted-foreground", approved: "bg-sky-100 text-sky-900 dark:bg-sky-900/50 dark:text-sky-100",
  rejected: "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100", deferred: "bg-violet-100 text-violet-900 dark:bg-violet-900/50 dark:text-violet-100",
  applied: "bg-emerald-600 text-white",
};

export default function AdminPriceReviewPage() {
  const { data: adminCheck, isLoading } = useQuery<{ isAdmin: boolean }>({ queryKey: ["/api/admin/check"], retry: false });
  const search = useSearch();
  const [location, setLocation] = useLocation();
  const params = new URLSearchParams(search);
  const tab = tabFromParam(params.get("tab"));
  const go = (v: string) => setLocation(v === "review" ? location : `${location}?tab=${v}`);
  if (isLoading) return <div className="p-6"><Skeleton className="h-8 w-64" /></div>;
  if (adminCheck?.isAdmin !== true) {
    return (
      <div className="p-6 max-w-lg mx-auto text-center space-y-2" data-testid="text-admin-required">
        <ShieldAlert className="h-8 w-8 mx-auto text-muted-foreground" />
        <h1 className="text-lg font-semibold">Admin only</h1>
        <p className="text-sm text-muted-foreground">Price Review is limited to builder admins.</p>
      </div>
    );
  }
  if (tab === "drafts") {
    return (
      <div className="w-full">
        <div className="px-4 sm:px-6 pt-4 sm:pt-6 max-w-7xl mx-auto">
          <Button variant="ghost" size="sm" onClick={() => setLocation(location)} data-testid="button-back-price-review"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Price Review</Button>
        </div>
        <AdminRecipeDraftsPage />
      </div>
    );
  }
  return (
    <Tabs value={tab} onValueChange={go} className="w-full">
      <div className="px-4 sm:px-6 pt-4 sm:pt-6 max-w-7xl mx-auto space-y-3">
        <div className="flex flex-wrap items-start gap-2">
          <div className="flex-1 min-w-[220px]">
            <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2" data-testid="text-page-title"><Scale className="h-5 w-5" /> Price Review</h1>
            <p className="text-muted-foreground text-sm max-w-3xl">Reported supplier research beside current development library values. Nothing changes without explicit approval and apply.</p>
          </div>
          <RefreshResearchButton />
        </div>
        <TabsList>
          <TabsTrigger value="review" data-testid="tab-review">Price Review</TabsTrigger>
          <TabsTrigger value="materials" data-testid="tab-materials">Materials</TabsTrigger>
          <TabsTrigger value="labour" data-testid="tab-labour">Labour</TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="review" className="mt-0"><ReviewOverviewTab onOpen={go} /></TabsContent>
      <TabsContent value="materials" className="mt-0"><PriceReviewWorkspace type="materials" /></TabsContent>
      <TabsContent value="labour" className="mt-0"><LabourWorkspace /></TabsContent>
      <div className="px-4 sm:px-6 pb-6 max-w-7xl mx-auto">
        <div className="rounded-md border p-3 flex flex-wrap items-center gap-3 text-sm" data-testid="section-advanced-tools">
          <Wrench className="h-4 w-4 text-muted-foreground" />
          <div className="flex-1 min-w-[220px]">
            <p className="font-medium">Advanced tools</p>
            <p className="text-xs text-muted-foreground">Work-item breakdown drafting proposes materials, quantities, waste, labour and plant splits for a work item without changing its headline rate. It is not market price research.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setLocation(`${location}?tab=drafts`)} data-testid="button-recipe-drafting">Work-item breakdown drafting</Button>
        </div>
      </div>
    </Tabs>
  );
}

function RefreshResearchButton() {
  const qc = useQueryClient();
  const refreshing = useIsFetching({ queryKey: getListPriceResearchQueryKey() }) > 0;
  return (
    <Button variant="outline" size="sm" disabled={refreshing}
      onClick={() => void qc.invalidateQueries({ queryKey: getListPriceResearchQueryKey() })}
      data-testid="button-refresh-research">
      <RefreshCw className={`h-4 w-4 mr-1.5${refreshing ? " animate-spin" : ""}`} /> Refresh
    </Button>
  );
}

function ReviewOverviewTab({ onOpen }: { onOpen: (tab: "materials" | "labour") => void }) {
  const listQ = usePriceResearchOverview();
  const overview = listQ.data ? asDoc<ReviewOverview>(listQ.data) : undefined;
  if (listQ.isLoading) return <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-3"><Skeleton className="h-20" /><Skeleton className="h-64" /></div>;
  if (listQ.isError || !overview) return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto"><Card><CardContent className="py-8 flex flex-col items-center gap-3 text-sm" data-testid="error-review-overview">
      <span className="text-destructive">Could not load price review history. {errorMessage(listQ.error)}</span>
      <Button variant="outline" size="sm" onClick={() => listQ.refetch()} data-testid="button-retry-review-overview">Retry</Button>
    </CardContent></Card></div>
  );
  return <PriceReviewHistory overview={overview} onOpen={onOpen} />;
}

function LabourWorkspace() {
  const listQ = usePriceResearchOverview();
  const overview = listQ.data ? asDoc<ReviewOverview>(listQ.data) : undefined;
  const labour = useMemo<LabourEntry[]>(() => overview?.labour ?? [], [overview]);
  const hasResearch = (overview?.batches ?? []).some((b) => batchType(b) === "labour");
  const [q, setQ] = useState("");
  const filtered = useMemo(() => { const s = q.trim().toLowerCase(); return labour.filter((l) => !s || `${l.taskName} ${l.taskId} ${l.unit}`.toLowerCase().includes(s)); }, [labour, q]);
  return (
    <div>
    {overview && !hasResearch && (
      <div className="px-4 sm:px-6 pt-4 sm:pt-6 max-w-7xl mx-auto">
        <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm" data-testid="banner-no-labour-research">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
          <div><p className="font-medium">No labour research uploaded</p><p className="text-xs text-muted-foreground">The allowances below are current development Cost Library values per work unit. Nothing here is researched or verified, and material proposals are not used as labour evidence. Download the labour template and import research to review it.</p></div>
        </div>
      </div>
    )}
    <PriceReviewWorkspace type="labour" />
    <div className="px-4 sm:px-6 pb-4 max-w-7xl mx-auto space-y-4">
      {listQ.isLoading ? <Skeleton className="h-64" /> : listQ.isError || !overview ? (
        <Card><CardContent className="py-8 flex flex-col items-center gap-3 text-sm" data-testid="error-labour">
          <span className="text-destructive">Could not load labour allowances. {errorMessage(listQ.error)}</span>
          <Button variant="outline" size="sm" onClick={() => listQ.refetch()} data-testid="button-retry-labour">Retry</Button>
        </CardContent></Card>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Hammer className="h-4 w-4" /> Current labour allowances</CardTitle>
            <CardDescription>{filtered.length} of {labour.length} work items · library values only</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search work item or unit" className="h-8 pl-8 text-sm" data-testid="input-search-labour" />
            </div>
            {labour.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground" data-testid="empty-labour">No work items with recipes found.</p>
              : filtered.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground" data-testid="empty-filtered-labour">No work items match.</p> : (
              <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
                <Table className="text-xs">
                  <TableHeader><TableRow><TableHead>Work item</TableHead><TableHead>Unit</TableHead><TableHead className="text-right">Labour allowance</TableHead><TableHead>Research</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {filtered.map((l) => (
                      <TableRow key={l.taskId} data-testid={`row-labour-${l.taskId}`}>
                        <TableCell><p className="font-medium text-sm">{l.taskName}</p><p className="text-muted-foreground">{l.taskId}{l.active ? "" : " · inactive"}</p></TableCell>
                        <TableCell className="text-muted-foreground">{l.unit || "—"}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{l.labourAllowance == null ? "Not set" : `${gbp(l.labourAllowance)} / ${l.unit || "unit"}`}</TableCell>
                        <TableCell className="text-muted-foreground">{hasResearch ? "See batches above" : "None supplied"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
    </div>
  );
}

function PriceReviewWorkspace({ type }: { type: ResearchType }) {
  const isLabour = type === "labour";
  const qc = useQueryClient();
  const { toast } = useToast();
  const listQ = usePriceResearchOverview();
  const overview = listQ.data ? asDoc<ReviewOverview>(listQ.data) : undefined;
  const batches = useMemo(() => (overview?.batches ?? []).filter((b) => batchType(b) === type), [overview, type]);
  const allCatalogue = useMemo(() => overview?.catalogue ?? [], [overview]);
  const catalogue = useMemo(() => allCatalogue.filter((c) => isLabourEntry(c) === isLabour), [allCatalogue, isLabour]);
  const catByKey = useMemo(() => new Map(allCatalogue.map((c) => [c.key, c])), [allCatalogue]);

  const [batchId, setBatchId] = useState<string | null>(null);
  const [detail, setDetail] = useState<BatchDetail | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [q, setQ] = useState(""); const [verdict, setVerdict] = useState("all"); const [decision, setDecision] = useState("all");
  const [unresolvedOnly, setUnresolvedOnly] = useState(false); const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<string>("25");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [applyPreview, setApplyPreview] = useState<ApplyPreview | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const detailM = useActOnPriceResearch();
  const decideM = useActOnPriceResearch();
  const previewApplyM = useActOnPriceResearch();
  const applyM = useActOnPriceResearch();
  const loadRef = useRef(detailM.mutate); loadRef.current = detailM.mutate;

  useEffect(() => { if (!batchId && batches.length) setBatchId(batches[0].id); }, [batches, batchId]);
  useEffect(() => {
    if (!batchId) return;
    setDetail(null); setSelected(new Set()); setPage(0);
    loadRef.current({ data: { action: "detail", batchId } }, { onSuccess: (d) => setDetail(asDoc<BatchDetail>(d)) });
  }, [batchId]);

  const rows = useMemo(() => detail?.rows ?? [], [detail]);
  const approvedRows = useMemo(() => rows.filter(r => r.decision === "approved"), [rows]);
  const retiredApprovedRows = useMemo(() => approvedRows.filter(r => r.targetKey && catByKey.get(r.targetKey)?.active === false), [approvedRows, catByKey]);
  const reviewableApprovedRows = useMemo(() => approvedRows.filter(r => !retiredApprovedRows.includes(r)), [approvedRows, retiredApprovedRows]);
  const rowsById = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => (verdict === "all" || r.verdict === verdict) && (decision === "all" || r.decision === decision) && (!unresolvedOnly || approvalBlocker(r) !== null)
      && (!s || `${r.materialName} ${r.spec} ${r.taskName} ${r.unit}`.toLowerCase().includes(s)));
  }, [rows, q, verdict, decision, unresolvedOnly]);
  const pageLimit = pageSize === "all" ? Math.max(1, filtered.length) : Number(pageSize);
  const pages = Math.max(1, Math.ceil(filtered.length / pageLimit));
  const pageRows = filtered.slice(page * pageLimit, page * pageLimit + pageLimit);
  useEffect(() => { if (page >= pages) setPage(0); }, [pages, page]);

  const counts = useMemo(() => {
    const c = { approved: 0, pending: 0, applied: 0, unresolved: 0, must: 0 };
    for (const r of rows) { if (r.decision === "approved") c.approved++; if (r.decision === "pending") c.pending++; if (r.decision === "applied") c.applied++; if (approvalBlocker(r) !== null) c.unresolved++; if (r.verdict === "Must be updated") c.must++; }
    return c;
  }, [rows]);
  const selectedRows = rows.filter((r) => selected.has(r.id));
  const missingPairIdList = useMemo(() => missingPairIds(selected, rows), [selected, rows]);
  const missingPairs = missingPairIdList.map((id) => `Paired ${rowsById.get(id)?.kind ?? "row"} not selected: ${rowsById.get(id)?.materialName ?? id} (${rowsById.get(id)?.taskName ?? ""})`);
  const unapprovedSelected = selectedRows.filter((r) => r.decision !== "approved" && r.decision !== "applied").length;
  const approveBlocked = selectedRows.filter((r) => r.decision !== "applied" && approvalBlocker(r) !== null);

  function onDoc(d: unknown, approvalIds: string[] = []) {
    const doc = asDoc<BatchDetail>(d);
    if (doc && Array.isArray(doc.rows)) {
      setDetail(doc);
      if (approvalIds.length) {
        const approved = new Set(doc.rows.filter(r => r.decision === "approved" && approvalIds.includes(r.id)).map(r => r.id));
        setSelected(previous => new Set([...previous].filter(id => !approved.has(id))));
      }
    }
    void qc.invalidateQueries({ queryKey: getListPriceResearchQueryKey() });
  }
  function saveChange(change: PriceResearchChange) {
    if (!detail) return;
    decideM.mutate({ data: { action: "decide", batchId: detail.id, revision: detail.revision, changes: [change] } }, {
      onSuccess: (d) => { onDoc(d, change.decision === "approved" ? [change.id] : []); setOpenId(null); toast({ title: change.decision ? `Marked ${DECISION_LABELS[change.decision]}` : "Edits saved", description: "Nothing applied to the library." }); },
      onError: (e) => toast({ title: "Could not save decision", description: errorMessage(e), variant: "destructive" }),
    });
  }
  function bulkDecide(dec: "approved" | "rejected" | "deferred" | "pending") {
    if (!detail || !selectedRows.length) return;
    const changes = selectedRows.filter((r) => r.decision !== "applied" && (dec !== "approved" || approvalBlocker(r) === null)).map((r) => ({ id: r.id, decision: dec }));
    const skipped = dec === "approved" ? approveBlocked : [];
    if (!changes.length) {
      if (skipped.length) {
        setOpenId(skipped[0].id);
        toast({ title: "No items approved yet", description: `${skipped.length} selected item(s) need details. ${skipped[0].materialName}: ${approvalBlocker(skipped[0])}. Check the purchasing unit, confirm the context and save your decision.` });
      }
      return;
    }
    decideM.mutate({ data: { action: "decide", batchId: detail.id, revision: detail.revision, changes } }, {
      onSuccess: (d) => { onDoc(d, dec === "approved" ? changes.map(c => c.id) : []); toast({ title: `${changes.length} marked ${DECISION_LABELS[dec]}`, description: skipped.length ? `${skipped.length} selected item(s) were NOT approved and remain selected. Use “Review item details” to resolve their purchasing units, context or missing values.` : undefined }); },
      onError: (e) => toast({ title: "Bulk decision failed", description: errorMessage(e), variant: "destructive" }),
    });
  }
  function startApply() {
    if (!detail) return;
    const ids = reviewableApprovedRows.map((r) => r.id);
    setConfirmed(false);
    setApplyPreview(null);
    setReviewError(null);
    setReviewOpen(true);
    if (!ids.length) return;
    previewApplyM.mutate({ data: { action: "preview-apply", batchId: detail.id, revision: detail.revision, selectedIds: ids } }, {
      onSuccess: (d) => { setApplyPreview(asDoc<ApplyPreview>(d)); },
      onError: (e) => setReviewError(errorMessage(e)),
    });
  }
  function confirmApply() {
    if (!detail || !applyPreview) return;
    applyM.mutate({ data: { action: "apply", batchId: detail.id, revision: detail.revision, selectedIds: applyPreview.selectedIds, token: applyPreview.token, confirm: true } }, {
      onSuccess: (d) => { onDoc(d); setReviewOpen(false); setApplyPreview(null); setSelected(new Set()); toast({ title: "Applied to development library", description: "Live prices are unchanged. Nothing has been published." }); },
      onError: (e) => toast({ title: "Apply failed", description: errorMessage(e), variant: "destructive" }),
    });
  }

  const selApproved = selectedRows.filter((r) => r.decision === "approved").length;
  const allPageSelected = pageRows.length > 0 && pageRows.every((r) => selected.has(r.id));
  const toggle = (id: string, on: boolean) => setSelected((p) => { const n = new Set(p); if (on) n.add(id); else n.delete(id); return n; });

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-5">
      {listQ.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-3"><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /></div>
      ) : listQ.isError || !overview ? (
        <Card><CardContent className="py-8 flex flex-col items-center gap-3 text-sm" data-testid="error-price-review">
          <span className="text-destructive">Could not load price research. {errorMessage(listQ.error)}</span>
          <Button variant="outline" size="sm" onClick={() => listQ.refetch()} data-testid="button-retry-price-review">Retry</Button>
        </CardContent></Card>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 p-3 text-sm" data-testid="banner-environment">
            {overview.canApply ? <ShieldCheck className="h-4 w-4 text-emerald-600" /> : <ShieldAlert className="h-4 w-4 text-amber-600" />}
            <span className="font-medium">Environment: <span data-testid="text-environment">{overview.environment === "live" ? "Live" : "Development"}</span></span>
            <span className="text-muted-foreground text-xs flex-1 min-w-[200px]">
              {overview.canApply ? "Confirming changes updates the development Cost Library only. Live prices are unchanged; publishing is separate." : "Applying is disabled in this environment. Research and decisions can still be reviewed."}
            </span>
            <Button variant="outline" size="sm" onClick={() => downloadPriceTemplate(type)} data-testid="button-template"><Download className="h-4 w-4 mr-1.5" /> {isLabour ? "Labour template" : "Template"}</Button>
            <Button size="sm" onClick={() => setImportOpen(true)} data-testid="button-open-import"><FileUp className="h-4 w-4 mr-1.5" /> Import {isLabour ? "labour " : ""}research</Button>
          </div>

          {batches.length === 0 ? (
            <Card><CardContent className="py-12 text-center space-y-3" data-testid="empty-batches">
              <FileUp className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="font-medium">{isLabour ? "No labour research batches yet" : "No research batches yet"}</p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">{isLabour ? "Upload a labour CSV keyed by task_id with allowances per work-item unit, plus any notes." : "Upload a price CSV, an optional factor CSV and any notes."} You will see a preview before anything is saved, and nothing is applied automatically.</p>
              <Button onClick={() => setImportOpen(true)} data-testid="button-empty-import">Import first batch</Button>
            </CardContent></Card>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={batchId ?? ""} onValueChange={setBatchId}>
                  <SelectTrigger className="w-full sm:w-96 h-9 text-sm" data-testid="select-batch"><SelectValue placeholder="Choose batch" /></SelectTrigger>
                  <SelectContent>
                    {batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} · {new Date(b.createdAt).toLocaleDateString("en-GB")} · {b.rowCount} rows · {b.appliedCount} applied</SelectItem>)}
                  </SelectContent>
                </Select>
                {detail && <span className="text-xs text-muted-foreground flex flex-wrap gap-x-2" data-testid="text-batch-revision">Revision {detail.revision} · files:
                  {detail.files.length === 0 ? " none" : detail.files.map((f, i) => { const u = safeFileUrl(f.downloadUrl); return u
                    ? <a key={f.id ?? i} href={u} download className="underline underline-offset-2 hover:text-foreground" data-testid={`link-file-${f.id ?? i}`}>{f.name} ({f.kind})</a>
                    : <span key={f.id ?? i}>{f.name} ({f.kind})</span>; })}</span>}
              </div>

              {detailM.isPending && !detail ? <Skeleton className="h-80" /> : detailM.isError && !detail ? (
                <Card><CardContent className="py-8 flex flex-col items-center gap-3 text-sm" data-testid="error-batch-detail">
                  <span className="text-destructive">Could not load batch. {errorMessage(detailM.error)}</span>
                  <Button variant="outline" size="sm" onClick={() => batchId && detailM.mutate({ data: { action: "detail", batchId } }, { onSuccess: (d) => setDetail(asDoc<BatchDetail>(d)) })} data-testid="button-retry-batch">Retry</Button>
                </CardContent></Card>
              ) : detail && (
                <>
                  {!!detail.warnings?.length && (
                    <ul className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2 text-xs space-y-0.5" data-testid="list-batch-warnings">
                      {detail.warnings.map((w, i) => <li key={i} className="flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600" />{w}</li>)}
                    </ul>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="stats-batch">
                    {[["Proposals", rows.length], ["Must be updated", counts.must], ["Needs attention", counts.unresolved], ["Approved, not applied", counts.approved], ["Applied", counts.applied]].map(([l, v]) => (
                      <div key={l as string} className="rounded-md border p-3"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">{l}</div><div className="text-xl font-semibold tabular-nums">{v}</div></div>
                    ))}
                  </div>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Proposals</CardTitle>
                      <CardDescription>{filtered.length} of {rows.length} · research labels are not approval · Claude reported prices are not independently verified · alternative rates are not additive</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        <div className="relative flex-1 min-w-[180px]">
                          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder={isLabour ? "Search work item or unit" : "Search material, spec, work item"} className="h-8 pl-8 text-sm" data-testid="input-search-proposals" />
                        </div>
                        <Select value={verdict} onValueChange={(v) => { setVerdict(v); setPage(0); }}>
                          <SelectTrigger className="w-full sm:w-44 h-8 text-xs" data-testid="select-filter-verdict"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="all" className="text-xs">All verdicts</SelectItem>{VERDICTS.map((v) => <SelectItem key={v} value={v} className="text-xs">{v}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={decision} onValueChange={(v) => { setDecision(v); setPage(0); }}>
                          <SelectTrigger className="w-full sm:w-40 h-8 text-xs" data-testid="select-filter-decision"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="all" className="text-xs">All decisions</SelectItem>{DECISIONS.map((d) => <SelectItem key={d} value={d} className="text-xs">{DECISION_LABELS[d]}</SelectItem>)}</SelectContent>
                        </Select>
                        <label className="flex items-center gap-1.5 text-xs px-2"><Checkbox checked={unresolvedOnly} onCheckedChange={(v) => { setUnresolvedOnly(v === true); setPage(0); }} data-testid="checkbox-filter-unresolved" /> Needs attention only</label>
                      </div>

                        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-md border bg-card p-2 text-xs" data-testid="bar-price-selection">
                          <span className="font-medium tabular-nums">{selected.size} selected · {selApproved} approved</span>
                          <span className="text-muted-foreground">{reviewableApprovedRows.length} ready for review{retiredApprovedRows.length > 0 ? ` · ${retiredApprovedRows.length} approved on retired work items` : ""}</span>
                          {missingPairs.length > 0 && <span className="text-amber-700 dark:text-amber-400" title={missingPairs.join("\n")} data-testid="text-missing-pairs">{missingPairs.length} paired row(s) must also be selected</span>}
                          {missingPairs.length > 0 && <Button size="sm" variant="outline" onClick={() => setSelected((p) => new Set([...p, ...missingPairIdList]))} data-testid="button-select-pairs">Select paired</Button>}
                          {approveBlocked.length > 0 && <Button size="sm" variant="outline" onClick={() => setOpenId(approveBlocked[0].id)} title={approveBlocked.map((r) => `${r.materialName}: ${approvalBlocker(r)}`).join("\n")} data-testid="button-review-blocked">{approveBlocked.length} not approved · Review item details</Button>}
                          {unapprovedSelected > 0 && <span className="text-muted-foreground">{unapprovedSelected} not yet approved</span>}
                          <div className="ml-auto flex flex-wrap gap-1.5">
                            <PriceReviewUndo detail={detail} researchType={type}
                              selectedIds={selectedRows.filter(r => r.decision === "applied").map(r => r.id)}
                              disabled={!overview.canApply || decideM.isPending || applyM.isPending}
                              onUndone={d => { onDoc(d); setSelected(new Set()); toast({ title: `Previous ${isLabour ? "labour" : "material"} rates restored`, description: "Selected proposals are Pending again. Live prices and saved quotes are unchanged." }); }} />
                            <Button size="sm" variant="outline" disabled={selected.size === 0} onClick={() => setSelected(new Set())} data-testid="button-clear-price-selection">Clear</Button>
                            <Button size="sm" variant="outline" disabled={decideM.isPending || selected.size === 0} onClick={() => bulkDecide("pending")} data-testid="button-bulk-pending">Unapprove</Button>
                            <Button size="sm" variant="outline" disabled={decideM.isPending || selectedRows.every(r => r.decision === "applied")} onClick={() => bulkDecide("approved")} data-testid="button-bulk-approve">Approve</Button>
                            <Button size="sm" variant="outline" disabled={decideM.isPending || selected.size === 0} onClick={() => bulkDecide("deferred")} data-testid="button-bulk-defer">Defer</Button>
                            <Button size="sm" variant="outline" disabled={decideM.isPending || selected.size === 0} onClick={() => bulkDecide("rejected")} data-testid="button-bulk-reject">Reject</Button>
                            <Button size="sm" variant="outline" disabled={!overview.canApply || approvedRows.length === 0 || previewApplyM.isPending}
                              onClick={startApply} data-testid="button-preview-apply">
                              {previewApplyM.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Review
                            </Button>
                          </div>
                        </div>

                      {filtered.length > 0 && <PriceReviewPagination position="top" page={page} pages={pages} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={size => { setPageSize(size); setPage(0); }} />}
                      {rows.length === 0 ? (
                        <p className="py-10 text-center text-sm text-muted-foreground" data-testid="empty-proposals">This batch has no proposals.</p>
                      ) : filtered.length === 0 ? (
                        <div className="py-10 text-center space-y-2" data-testid="empty-filtered-proposals">
                          <p className="text-sm text-muted-foreground">No proposals match these filters.</p>
                          <Button size="sm" variant="outline" onClick={() => { setQ(""); setVerdict("all"); setDecision("all"); setUnresolvedOnly(false); }} data-testid="button-reset-price-filters">Reset filters</Button>
                        </div>
                      ) : (
                        <div className="overflow-x-auto -mx-2 sm:mx-0">
                          <Table className="text-xs">
                            <TableHeader><TableRow>
                              <TableHead className="w-8"><Checkbox checked={allPageSelected} onCheckedChange={(v) => setSelected((p) => { const n = new Set(p); for (const r of pageRows) { if (v === true) n.add(r.id); else n.delete(r.id); } return n; })} aria-label="Select page" data-testid="checkbox-select-page" /></TableHead>
                              <TableHead>{isLabour ? "Allowance" : "Material"}</TableHead><TableHead className="hidden md:table-cell">Work item</TableHead>
                              <TableHead className="text-right">Library now</TableHead><TableHead className="text-right">Claude reported</TableHead><TableHead className="text-right">Difference</TableHead>
                              <TableHead>Research</TableHead><TableHead>Decision</TableHead>
                            </TableRow></TableHeader>
                            <TableBody>
                              {pageRows.map((r) => <ProposalRow key={r.id} r={r} byKey={catByKey} checked={selected.has(r.id)} onCheck={(on) => toggle(r.id, on)} onOpen={() => setOpenId(r.id)} />)}
                            </TableBody>
                          </Table>
                          <PriceReviewPagination position="bottom" page={page} pages={pages} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={size => { setPageSize(size); setPage(0); }} />
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <details className="rounded-md border p-3 text-sm" data-testid="section-history">
                    <summary className="cursor-pointer font-medium flex items-center gap-1.5"><History className="h-4 w-4" /> Audit history ({detail.history.length})</summary>
                    <ul className="mt-2 space-y-1 text-xs max-h-64 overflow-y-auto">
                      {detail.history.length === 0 ? <li className="text-muted-foreground">No decisions recorded yet.</li> : detail.history.map((h, i) => (
                        <li key={i} className="flex flex-wrap gap-x-3 border-b last:border-0 pb-1">
                          {Object.entries(h).map(([k, v]) => <span key={k}><span className="text-muted-foreground">{k}</span> {typeof v === "object" ? JSON.stringify(v) : String(v)}</span>)}
                        </li>
                      ))}
                    </ul>
                  </details>
                </>
              )}
            </>
          )}
        </>
      )}

      <PriceReviewImportDialog researchType={type} open={importOpen} onOpenChange={setImportOpen} onSaved={(id) => { if (id) setBatchId(id); }} />
      <PriceReviewProposalDialog row={openId ? rowsById.get(openId) ?? null : null} rowsById={rowsById} catalogue={catalogue} saving={decideM.isPending} onClose={() => setOpenId(null)} onSave={saveChange} onOpenRow={setOpenId} />

      <Dialog open={reviewOpen} onOpenChange={(o) => { if (!applyM.isPending) { setReviewOpen(o); if (!o) setApplyPreview(null); } }}>
        <DialogContent className="max-w-4xl max-h-[92dvh] overflow-y-auto" data-testid="dialog-apply-impact">
          <DialogHeader>
            <DialogTitle>Review approved {isLabour ? "labour" : "material"} changes</DialogTitle>
            <DialogDescription>Check your approved items, then confirm to apply them to the development Cost Library only. Live prices, personal overrides and saved quotes are unchanged. This does not publish anything.</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">{isLabour
            ? "These changes update only the labour allowance per work-item unit. Existing material and plant costs are retained."
            : "Price changes update the cost per purchase unit. Quantity / waste changes update how much material the work item uses; they do not change its unit price."}</p>
          {retiredApprovedRows.length > 0 && <div className="rounded-md border bg-muted/40 p-3 text-sm" data-testid="notice-retired-approved">
            <p className="font-medium">{retiredApprovedRows.length} approved proposal(s) belong to retired work items and will not be applied.</p>
            <p className="text-xs text-muted-foreground">Your approvals are retained. These items do not block active changes, and no work items will be reactivated.</p>
            <ul className="mt-2 list-disc pl-4 text-xs">{retiredApprovedRows.map(r => <li key={r.id}>{r.materialName} — {r.taskName}</li>)}</ul>
          </div>}
          {(
            <>
              {previewApplyM.isPending && <p className="text-sm text-muted-foreground">Preparing approved changes…</p>}
              {reviewError && <p className="text-sm text-destructive" role="alert">{reviewError}</p>}
              <div className="overflow-x-auto rounded-md border">
                <Table className="text-xs">
                  <TableHeader><TableRow><TableHead>{isLabour ? "Labour" : "Material"}</TableHead><TableHead>Work item</TableHead><TableHead className="text-right">Current</TableHead><TableHead className="text-right">Approved update</TableHead><TableHead className="text-right">Difference</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {reviewableApprovedRows.length === 0 && <TableRow><TableCell colSpan={5}>No active approved changes to apply.</TableCell></TableRow>}
                    {reviewableApprovedRows.map((r) => {
                      const currentTarget = applyPreview?.currentTargets?.find(c => c.key === r.targetKey);
                      const currentPrice = currentTarget?.price ?? libraryPrice(r, catByKey);
                      const diff = r.kind === "factor" ? null : priceDiff(currentPrice, r.proposedPrice);
                      return (
                      <TableRow key={r.id} data-testid={`row-approved-${r.id}`}>
                        <TableCell className="font-medium">{r.materialName}<p className="font-normal text-muted-foreground">{r.spec}</p><Badge variant="outline" className="text-[10px]">{proposalUpdateLabel(r)}</Badge></TableCell>
                        <TableCell>{r.taskName}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.kind === "factor" ? <>{currentTarget?.factor ?? r.currentFactor ?? "—"} / unit · {currentTarget?.waste ?? r.currentWaste ?? "—"}% waste</> : <>{gbp(currentPrice)} / {r.unit}</>}</TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{r.kind === "factor" ? <>{r.proposedFactor ?? currentTarget?.factor ?? r.currentFactor ?? "—"} / unit · {r.proposedWaste ?? currentTarget?.waste ?? r.currentWaste ?? "—"}% waste<div className="font-normal text-muted-foreground">Current unit price: {gbp(currentPrice)} / {r.unit}</div></> : <>{priceRecommendation(r)}{r.proposedPrice != null && <> / {r.proposedUnit || r.unit}</>}</>}</TableCell>
                        <TableCell data-testid={`difference-approved-${r.id}`} className={`text-right tabular-nums whitespace-nowrap ${diff && diff.abs < 0 ? "text-emerald-700 dark:text-emerald-400" : diff && diff.abs > 0 ? "text-rose-700 dark:text-rose-400" : "text-muted-foreground"}`}>
                          {diff ? <>{signedGbp(diff.abs)}<div className="text-[10px]">{signedPct(diff.pct)}</div></> : r.kind === "factor" ? "Quantity / waste only" : retainsExistingPrice(r) ? "Unchanged" : "No proposed price"}
                        </TableCell>
                      </TableRow>
                    ); })}
                  </TableBody>
                </Table>
              </div>
              <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(v === true)} data-testid="checkbox-confirm-apply" />
                <span>I confirm these approved changes for the development Cost Library only.</span>
              </label>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" disabled={applyM.isPending} onClick={() => setApplyPreview(null)} data-testid="button-cancel-apply">Cancel</Button>
            <Button disabled={!confirmed || !applyPreview || !!reviewError || applyM.isPending} onClick={confirmApply} data-testid="button-confirm-apply">
              {applyM.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Confirm and apply to development
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProposalRow({ r, byKey, checked, onCheck, onOpen }: { r: Proposal; byKey: Map<string, CatalogueEntry>; checked: boolean; onCheck: (on: boolean) => void; onOpen: () => void }) {
  const current = libraryPrice(r, byKey);
  const diff = priceDiff(current, r.proposedPrice);
  const unitChange = r.proposedUnit && r.proposedUnit !== r.unit;
  return (
    <TableRow className="cursor-pointer" onClick={onOpen} data-testid={`row-proposal-${r.id}`}>
      <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={checked} onCheckedChange={(v) => onCheck(v === true)} aria-label={`Select ${r.materialName}`} data-testid={`checkbox-proposal-${r.id}`} /></TableCell>
      <TableCell className="max-w-[260px]">
        <p className="font-medium text-sm truncate" title={r.materialName}>{r.materialName}</p>
        <p className="text-muted-foreground truncate">{r.spec} · {r.unit}</p>
        <Badge variant="outline" className="text-[10px]">{proposalUpdateLabel(r)}</Badge>
        {r.targetKey && byKey.get(r.targetKey)?.active === false && <Badge variant="outline" className="text-[10px]">Retired work item · not applicable</Badge>}
        <p className="md:hidden text-muted-foreground truncate">{r.taskName}</p>
      </TableCell>
      <TableCell className="hidden md:table-cell text-muted-foreground max-w-[200px] truncate" title={r.taskName}>{r.taskName || "—"}</TableCell>
      <TableCell className="text-right tabular-nums whitespace-nowrap">{r.kind === "factor" ? `x${r.currentFactor ?? "—"} / ${r.currentWaste ?? "—"}%` : gbp(current)}</TableCell>
      <TableCell className="text-right tabular-nums whitespace-nowrap font-medium">
        {r.kind === "factor" ? `x${r.proposedFactor ?? "—"} / ${r.proposedWaste ?? "—"}%` : priceRecommendation(r)}
        {unitChange && <div className={`text-[10px] ${approvalBlocker(r) ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"}`}>per {r.proposedUnit}</div>}
      </TableCell>
      <TableCell className={`text-right tabular-nums whitespace-nowrap ${diff && diff.abs > 0 ? "text-rose-700 dark:text-rose-400" : diff && diff.abs < 0 ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}`} data-testid={`text-diff-${r.id}`}>
        {diff ? <>{signedGbp(diff.abs)}<div className="text-[10px]">{signedPct(diff.pct)}</div></> : "—"}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          <Badge className={`text-[10px] no-default-hover-elevate ${VERDICT_TONE[r.verdict] ?? ""}`} data-testid={`status-verdict-${r.id}`}>{r.verdict}</Badge>
          {approvalBlocker(r) && <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-800 dark:text-amber-300" title={approvalBlocker(r)!}>Needs attention</Badge>}
          {r.pairedIds.length > 0 && <Badge variant="outline" className="text-[10px]">Paired {r.pairedIds.length}</Badge>}
        </div>
      </TableCell>
      <TableCell><Badge className={`text-[10px] no-default-hover-elevate ${DECISION_TONE[r.decision]}`} data-testid={`status-decision-${r.id}`}>{DECISION_LABELS[r.decision]}</Badge></TableCell>
    </TableRow>
  );
}
